import type { DealsLandingState } from '@typings/deals';
import type { ActionTypes as DealsLandingActions } from '@typings/deals/actionTypes';
import type { SetDealsPageStateAction } from '@typings/deals/actionTypes';
import {
	SET_CATEGORIES,
	SET_CATEGORY_CODE_FILTER,
	SET_DEALS_PAGE_STATE,
	SET_DEAL_TYPE_FAILURE,
	SET_DEAL_TYPE_FILTER,
	SET_DEAL_TYPE_REQUEST,
	SET_DEAL_TYPE_SUCCESS,
	SET_LOCATION_FILTER,
	UPDATE_CATEGORY_VENDOR_COUNTS,
} from './actionTypes';
import { initialState } from './initialState';

/**
 * Deals landing page reducer - handles all state updates for deals filtering and data management
 * Manages user filter selections, loading states, and category data with vendor counts
 *
 * @param state - Current deals landing state, undefined during initialization
 * @param action - Action object with type and payload
 * @returns Updated state based on the action type
 */
export default function reducer(
	state: DealsLandingState | undefined,
	action: DealsLandingActions,
): DealsLandingState {
	if (state === undefined) return initialState;
	switch (action.type) {
		case SET_DEALS_PAGE_STATE: {
			const payload = (action as SetDealsPageStateAction).payload;
			return {
				...state,
				filters: {
					...state.filters,
					dealType: payload.dealType,
					location: {
						city: payload.location.city ?? '',
						stateCode: payload.location.state ?? '',
					},
					categoryCode: payload.selectedCategoryCode ?? null,
				},
				categories: payload.categories ?? state.categories,
				loading: false,
				error: null,
			};
		}
		case SET_DEAL_TYPE_FILTER: {
			return {
				...state,
				filters: {
					...state.filters,
					dealType: action.payload.dealType,
				},
			};
		}
		case SET_DEAL_TYPE_REQUEST: {
			return {
				...state,
				filters: {
					...state.filters,
					dealType: action.payload.dealType,
				},
				loading: true,
				error: null,
			};
		}
		case SET_DEAL_TYPE_SUCCESS: {
			return { ...state, loading: false };
		}
		case SET_DEAL_TYPE_FAILURE: {
			return { ...state, loading: false, error: action.payload.error };
		}
		case SET_LOCATION_FILTER: {
			return {
				...state,
				filters: {
					...state.filters,
					location: { ...action.payload },
				},
			};
		}
		case SET_CATEGORIES: {
			return {
				...state,
				categories: action.payload,
			};
		}
		case SET_CATEGORY_CODE_FILTER: {
			return {
				...state,
				filters: {
					...state.filters,
					categoryCode: action.payload.categoryCode,
				},
			};
		}
		case UPDATE_CATEGORY_VENDOR_COUNTS: {
			if (!state.categories) {
				return state;
			}
			return {
				...state,
				categories: state.categories.map((category) => ({
					...category,
					vendorCount: action.payload[category.id] ?? 0,
				})),
			};
		}
		default:
			return state;
	}
}
